import { Anchor, Badge } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

type UnreadResponse = { ok: boolean; count: number };

const UNREAD_KEY = ['notifications', 'unread-count'] as const;

/**
 * Notifications nav anchor with an unread-count Indicator overlay.
 *
 * Uses TanStack Query with a 60s refetchInterval and refetch-on-focus so
 * the badge stays roughly current without holding a websocket open just
 * for the count. Fails silently — a network blip should never put a stale
 * red dot on the UI; we treat any error as `count = 0`.
 *
 * Renders only when signed in (parent gates this).
 */
export function NotificationsNavLink({
  onClick,
  size = 'sm',
  drawerVariant = false,
}: {
  onClick?: () => void;
  size?: 'sm' | 'md';
  drawerVariant?: boolean;
}) {
  const { data } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: async (): Promise<UnreadResponse> => {
      const res = await fetch('/api/notifications/unread-count', {
        credentials: 'include',
      });
      if (!res.ok) return { ok: false, count: 0 };
      return res.json();
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // Cap displayed count quietly — if a user has 999+ unread we have bigger
    // problems. Treat negative / NaN as zero defensively.
    select: (d) => ({ ...d, count: Math.max(0, Math.floor(d?.count ?? 0)) }),
  });
  const count = data?.count ?? 0;
  const label = count > 99 ? '99+' : String(count);

  // Top-bar variant uses an inline-flex Anchor so the bell + text live in the
  // same vertical box as the other plain-text nav anchors. Without this, the
  // SVG's `display: block` shifts the icon-containing anchor a couple of
  // pixels higher than its siblings in the parent <Group>.
  const linkProps = {
    component: Link,
    to: '/notifications',
    c: drawerVariant ? 'ink.9' : 'ink.8',
    fw: 500,
    size,
    onClick,
    style: drawerVariant
      ? undefined
      : ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          lineHeight: 1.55,
        } as const),
  } as const;

  // Top bar: render count as an inline Badge AFTER the "Notifications" text
  // (was an Indicator overlay; the overlay's anchored corner pushed the
  // count pill leftward over the bell icon, looking like the badge was
  // covering it). Inline flows naturally and is easier to read.
  // Drawer variant has no bell, so the badge appears inline next to the
  // text label there using the same approach.
  if (drawerVariant) {
    if (count === 0) {
      return (
        <Anchor {...linkProps} aria-label="Notifications (none unread)">
          <span>Notifications</span>
        </Anchor>
      );
    }
    return (
      <Anchor {...linkProps} aria-label={`${count} unread notifications`}>
        <span>Notifications</span>
        <Badge color="brand.6" size="sm" variant="filled" radius="xl">
          {label}
        </Badge>
      </Anchor>
    );
  }

  if (count === 0) {
    return (
      <Anchor {...linkProps} aria-label="Notifications (none unread)">
        <BellIcon />
        <span>Notifications</span>
      </Anchor>
    );
  }
  return (
    <Anchor {...linkProps} aria-label={`${count} unread notifications`}>
      <BellIcon />
      <span>Notifications</span>
      <Badge color="brand.6" size="sm" variant="filled" radius="xl">
        {label}
      </Badge>
    </Anchor>
  );
}

// Inline bell SVG — avoids pulling in an icon library for one glyph. The
// stroke colour rides currentColor so it matches the surrounding Anchor's
// `c` prop (ink.8 in the header, ink.9 in the drawer if we ever surface it
// there).
function BellIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
